<template>
  <div class="input-group">
    <div>
      <div class="input-title">Text-to-Speech (TTS)</div>
      <v-row class="m-l-align">
        <v-col class="m-t-5" cols="12">
          <v-autocomplete
            class="m-t-5 no-drag"
            v-model="property.tts.voice"
            :items="ttsChoices"
            item-text="allText"
            item-value="voice"
            label="Voice"
            placeholder="Voice"
            dense
            hide-details
          >
            <template slot="selection" slot-scope="{ item }">
              {{ subString(item.name, 70) }}
            </template>
            <template slot="item" slot-scope="{ item }">
              <div
                class="float-right width100 font-size-sub margin8px m-r-5 row-center"
              >
                <div>
                  <img
                    v-if="item.icon"
                    :src="item.icon"
                    :alt="item.engine"
                    width="20"
                    class="m-r-15"
                  />
                </div>
                <div>
                  <div class="dropdown-title">
                    {{ subString(item.name, 50) }}
                  </div>
                  <div class="dropdown-text">
                    {{ subString(item.description, 70) }}
                  </div>
                </div>
              </div>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>
      <v-row class="m-l-align">
        <v-col cols="2" class="p-t-0 p-b-0">
          Speed
        </v-col>
        <v-col cols="10" class="p-t-0 p-b-0">
          <div class="d-flex align-center justify-start">
            <v-slider
              v-model="property.tts.speed"
              :min="minSpeed"
              :max="maxSpeed"
              :step="stepSpeed"
              hide-details
              class="slider-scroll"
            ></v-slider>
            <span class="slider-text m-l-20">
              {{ property.tts.speed ? property.tts.speed.toFixed(2) : '' }}
            </span>
          </div>
        </v-col>
        <v-col
          v-if="!['botnoi', 'botnoiv3'].includes(engine)"
          cols="2"
          class="p-t-0"
        >
          Pitch
        </v-col>
        <v-col
          v-if="!['botnoi', 'botnoiv3'].includes(engine)"
          cols="10"
          class="p-t-0"
        >
          <div class="d-flex align-center justify-start">
            <v-slider
              v-model="property.tts.pitch"
              :min="minPitch"
              :max="maxPitch"
              :step="stepPitch"
              hide-details
              class="slider-scroll"
            >
            </v-slider>
            <span class="slider-text m-l-20">
              {{ property.tts.pitch ? property.tts.pitch.toFixed(2) : '' }}
            </span>
          </div>
        </v-col>
        <v-col cols="12" class="p-t-0 pb-5 d-flex align-end">
          <v-select
            v-model="property.tts.dictionaries"
            label="Dictionaries"
            :items="ttsDictionaries"
            item-text="name"
            item-value="tts_dictionary_id"
            dense
            hide-details
            multiple
            chips
            clearable
            clear-icon="fa-times"
          >
            <template v-slot:selection="data">
              <v-tooltip
                v-if="data.item.description"
                :max-width="350"
                :open-delay="700"
                top
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-chip v-on="on" v-bind="attrs">{{ data.item.name }}</v-chip>
                </template>
                <div class="text-block">{{ data.item.description }}</div>
              </v-tooltip>
              <v-chip v-else>
                {{ data.item.name }}
              </v-chip>
            </template>
            <template v-slot:item="data">
              <v-tooltip
                v-if="data.item.description"
                :max-width="350"
                :open-delay="700"
                top
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-layout wrap v-on="on" v-bind="attrs">
                    <v-list-item-action class="m-r-10">
                      <v-checkbox v-model="data.attrs.inputValue" />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ data.item.name }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-layout>
                </template>
                <div class="text-block">{{ data.item.description }}</div>
              </v-tooltip>
              <v-layout v-else wrap>
                <v-list-item-action class="m-r-10">
                  <v-checkbox v-model="data.attrs.inputValue" />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                </v-list-item-content>
              </v-layout>
            </template>
          </v-select>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small
                v-bind="attrs"
                v-on="on"
                class="m-l-10"
                @click="goToDictionary"
              >
                fa-arrow-up-right-from-square
              </v-icon>
            </template>
            <span>Open TTS dictionaries</span>
          </v-tooltip>
        </v-col>
      </v-row>
      <v-row class="m-l-align">
        <v-col class="d-flex align-start p-t-0" cols="12" sm="12" lg="12">
          <v-textarea
            v-model="text"
            label="Enter text"
            placeholder="Enter text to speak …"
            class="textarea-input no-drag"
            solo
            hide-details
            rows="2"
            row-height="20"
          ></v-textarea>

          <div>
            <div>
              <v-progress-circular
                v-if="ttsLoading"
                class="m-l-10"
                indeterminate
                :size="16"
                :width="2"
              ></v-progress-circular>
              <v-icon
                v-else
                class="m-l-10"
                small
                :disabled="!text"
                @click="playPrompt('tts')"
              >
                {{
                  isPlaying && typePlaying === 'tts'
                    ? 'fa-stop-circle'
                    : 'fa-play-circle'
                }}
              </v-icon>
            </div>
            <div>
              <v-progress-circular
                v-if="ttsDownloading"
                class="m-l-10"
                indeterminate
                :size="16"
                :width="2"
              ></v-progress-circular>
              <v-icon
                v-else
                class="m-l-10"
                small
                :disabled="!text"
                @click="downloadPrompt('tts')"
              >
                fa-arrow-alt-circle-down
              </v-icon>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>

    <div class="m-t-15">
      <config-asr
        :defaultInput="property.default_input"
        :filterConfig="filterConfig"
      ></config-asr>
    </div>

    <div class="m-t-15">
      <config-nlu :defaultInput="property.default_input"></config-nlu>
    </div>

    <div class="m-t-15">
      <div class="input-title">Input</div>
      <v-row class="p-b-0">
        <v-col class="p-b-5" cols="12" sm="12" lg="12">
          <div class="d-flex align-end">
            <v-checkbox
              v-model="audioEnable"
              label="Beep"
              hide-details
            ></v-checkbox>
            <v-select
              v-model="audioSelected"
              class="m-l-50"
              :items="beepAudios"
              :disabled="!audioEnable"
              item-text="path"
              item-value="path"
              placeholder="*.wav file"
              dense
              hide-details
            ></v-select>
            <v-icon
              class="m-l-10"
              :disabled="!audioEnable || !audioSelected"
              small
              @click="playPrompt('beep')"
            >
              {{
                isPlaying && typePlaying === 'beep'
                  ? 'fa-stop-circle'
                  : 'fa-play-circle'
              }}
            </v-icon>
            <v-icon
              class="m-l-5"
              :disabled="!audioEnable"
              small
              @click="dialog = true"
            >
              fa-plus-circle
            </v-icon>
            <v-icon
              class="m-l-5"
              :disabled="!audioEnable || !audioSelected"
              small
              @click="deleteAudio(audioSelected)"
            >
              fa-trash
            </v-icon>
          </div>
          <v-checkbox
            v-model="property.default_input.is_count_retry"
            label="+1 retry if there is no input"
            hide-details
          ></v-checkbox>
        </v-col>
      </v-row>
    </div>

    <div class="m-t-15">
      <div class="input-title">Flow</div>
      <v-row class="m-l-align">
        <v-col cols="12" sm="7" lg="7">
          <v-tooltip left :max-width="350">
            <template v-slot:activator="{ on }">
              <div v-on="on">
                Fallback after
              </div>
            </template>
            <span>
              Maximum number of failed attempts
            </span>
          </v-tooltip>
        </v-col>
        <v-col class="d-flex" cols="12" sm="5" lg="5">
          <div>
            <v-text-field
              v-model="property.flow.fallback_after"
              type="number"
              step="1"
              min="1"
              dense
              reverse
              hide-details
              class="margin0 padding0 width50px"
              @blur="setPropertyData('fallback_after')"
            ></v-text-field>
          </div>
          <div class="ml-2">attempts</div>
        </v-col>
      </v-row>
      <v-row class="m-l-align">
        <v-col class="p-t-0" cols="12" sm="12" lg="12">
          <v-select
            class="m-auto"
            v-model="property.flow.page_max_retry"
            :items="tabFilter"
            item-text="name"
            item-value="id"
            label="On max retry, jump to page"
            dense
            hide-details
            clearable
            clear-icon="fa-times"
          ></v-select>
        </v-col>
      </v-row>
      <v-row class="m-l-align">
        <v-col class="p-t-1" cols="12" sm="12" lg="12">
          <v-select
            class="m-auto"
            v-model="property.flow.page_error"
            :items="tabFilter"
            item-text="name"
            item-value="id"
            label="On error, jump to page"
            dense
            hide-details
            clearable
            clear-icon="fa-times"
          ></v-select>
        </v-col>
      </v-row>
      <v-row class="m-l-align">
        <v-col class="p-t-0" cols="12" sm="12" lg="12">
          <v-select
            class="m-auto"
            v-model="property.flow.page_session_ended"
            :items="tabFilter"
            item-text="name"
            item-value="id"
            label="On session ended, jump to page"
            dense
            hide-details
            clearable
            clear-icon="fa-times"
          ></v-select>
        </v-col>
      </v-row>
    </div>

    <div class="m-t-15">
      <div class="input-title">Machine Detection</div>
      <v-row>
        <v-col class="p-b-0" cols="12" sm="12" lg="12">
          <div class="d-flex">
            <v-checkbox v-model="property.amd.enable" hide-details></v-checkbox>
            <v-tooltip left :max-width="350">
              <template v-slot:activator="{ on, attrs }">
                <div
                  v-bind="attrs"
                  v-on="on"
                  @click="asr.voice_barge_in = !asr.voice_barge_in"
                >
                  Enable Machine Detection at start of call
                </div>
              </template>
              <span>
                At the start of the call, attempt to check whether the answering
                party is human or machine.<br /><br />
                The result is kept in ${START.result} which has 3 possible
                values: human, machine, unknown.
              </span>
            </v-tooltip>
          </div>
        </v-col>
      </v-row>

      <v-row class="align-center">
        <v-col class="p-b-0" cols="12" sm="7" lg="7">
          <v-tooltip left :max-width="350">
            <template v-slot:activator="{ on }">
              <div class="m-l-asr" v-on="on">
                Max duration
              </div>
            </template>
            <span>
              Maximum number of seconds that the system should try to analyze
              before timing out and return unknown.<br /><br />
              Default value is 10
            </span>
          </v-tooltip>
        </v-col>
        <v-col class="middle d-flex align-center p-b-0" cols="12" sm="5" lg="5">
          <div>
            <v-text-field
              class="margin0 padding0 width70px"
              v-model="property.amd.max_duration"
              type="number"
              min="0"
              step="1"
              @blur="setPropertyData('max_duration')"
              :disabled="!property.amd.enable"
              hide-details
              dense
              reverse
            ></v-text-field>
          </div>
          <div class="ml-2">seconds</div>
        </v-col>
      </v-row>

      <v-row class="align-center">
        <v-col class="p-b-0" cols="12" sm="7" lg="7">
          <v-tooltip left :max-width="350">
            <template v-slot:activator="{ on }">
              <div class="m-l-asr" v-on="on">
                Silence threshold
              </div>
            </template>
            <span>
              Number of seconds of continuous silence. Continuous silence longer
              than the threshold will be interpreted as human i.e. human tends
              to say hello then long pause for other party to speak.<br /><br />
              This number should be set slightly higher than pauses found in
              answering machines.<br /><br />
              Default value is 3
            </span>
          </v-tooltip>
        </v-col>
        <v-col class="middle d-flex align-center p-b-0" cols="12" sm="5" lg="5">
          <div>
            <v-text-field
              class="margin0 padding0 width70px"
              v-model="property.amd.max_silence"
              type="number"
              min="0"
              step="1"
              @blur="setPropertyData('max_silence')"
              :disabled="!property.amd.enable"
              hide-details
              dense
              reverse
            ></v-text-field>
          </div>
          <div class="ml-2">seconds</div>
        </v-col>
      </v-row>

      <v-row class="align-center">
        <v-col class="p-b-0" cols="12" sm="7" lg="7">
          <v-tooltip left :max-width="350">
            <template v-slot:activator="{ on }">
              <div class="m-l-asr" v-on="on">
                Speech threshold
              </div>
            </template>
            <span>
              Number of seconds of speech activity. If there is no continuous
              silence, system will decide based on total duration of all voices.
              Duration lower than the threshold will be interpreted as human
              (i.e. human tends to say short sentences like hello then pause for
              other party to speak), duration higher will be interpreted as
              machine (i.e. machine tends to speak long sentences and rarely
              pauses).<br /><br />
              This number should be set slightly higher than the duration of
              human usual greetings, but lower than answering machine
              responses.<br /><br />
              Default value is 5
            </span>
          </v-tooltip>
        </v-col>
        <v-col class="middle d-flex align-center p-b-0" cols="12" sm="5" lg="5">
          <div>
            <v-text-field
              class="margin0 padding0 width70px"
              v-model="property.amd.machine_cutoff_duration"
              type="number"
              min="0"
              step="1"
              @blur="setPropertyData('machine_cutoff_duration')"
              :disabled="!property.amd.enable"
              hide-details
              dense
              reverse
            ></v-text-field>
          </div>
          <div class="ml-2">seconds</div>
        </v-col>
      </v-row>
    </div>

    <audio ref="myAudio" autoload>
      <source :src="previewVoice" type="audio/wav" />
      Your browser does not support the audio element.
    </audio>

    <hr class="note-divider" />
    <note-component :node="node" />

    <dialog-manage
      v-if="dialog"
      :dialog="dialog"
      :editedIndex="editedIndex"
      :editedItem="editedItem"
      :errorMessage="errorMessage"
      @close="close"
      @save="save"
    />
    <dialog-confirm
      v-if="dialogConfirm.open"
      :dialogConfirm="dialogConfirm"
      @dialogConfirmClick="dialogConfirmClick"
    />
    <snackbar v-if="snackbar.open" :snackbar="snackbar" />
  </div>
</template>

<script>
import _ from 'lodash'
import apiAudio from '@/apis/audio'
import apiSynthesis from '@/apis/synthesis'
import apiTtsDictionaries from '@/apis/tts_dictionaries'

import NoteComponent from '../../../components/note'

import DialogManage from './components/dialog_manage'
import DialogConfirm from './components/dialog_confirm'
import Snackbar from './components/snackbar'

import ConfigAsr from './components/config_asr'
import ConfigNlu from './components/config_nlu'

export default {
  props: {
    node: Object,
    tabs: Array,
    ttsChoices: Array
  },
  components: {
    NoteComponent,
    DialogManage,
    DialogConfirm,
    Snackbar,
    ConfigAsr,
    ConfigNlu
  },
  data() {
    return {
      num: 5.0,
      property: {},
      tabFilter: [],
      defaultText: 'ยินดีต้อนรับสู่บริการ virtual agent',
      text: '',
      defaultSpeed: 1,
      minSpeed: 0.5,
      maxSpeed: 1.5,
      stepSpeed: 0.05,
      defaultPitch: 1,
      minPitch: 0.5,
      maxPitch: 1.5,
      stepPitch: 0.05,
      isPlaying: false,
      typePlaying: '',
      previewVoice: '',
      engine: '',
      beepAudios: [],
      dialog: false,
      editedIndex: -1,
      editedItem: {
        _id: '',
        path: '',
        text: '',
        shortPath: '',
        category_code: 'BEEP',
        file: null
      },
      defaultItem: {
        _id: '',
        path: '',
        text: '',
        shortPath: '',
        category_code: 'BEEP',
        file: null
      },
      minioHost: '',
      errorMessage: '',
      snackbar: {
        open: false,
        colorSnackbar: '',
        textSnackbar: ''
      },
      audioEnable: true,
      audioSelected: '',
      signedUrlSelect: '',
      dialogConfirm: {
        open: false,
        _id: '',
        name: '',
        action: ''
      },
      filterConfig: 'all',
      ttsLoading: false,
      ttsDownloading: false,
      ttsDictionaries: []
    }
  },
  watch: {
    'property.tts.voice': {
      // immediate: true,
      handler(value) {
        if (value) {
          const item = _.find(this.ttsChoices, { voice: value })
          this.engine = item ? item.engine.toLowerCase() : ''
          this.property.tts.speaker = item.speaker || ''
          if (!['botnoi', 'botnoiv3'].includes(this.engine)) {
            if (!this.property.tts.pitch) {
              this.property.tts.pitch = this.defaultPitch
            }
          } else {
            this.property.tts.pitch = undefined
          }
          this.text = item.sample_utterance || this.defaultText
        } else {
          const item = _.find(this.ttsChoices, { isDefault: true })
          if (item && this.property.tts) this.property.tts.voice = item.voice
        }
      }
    },
    'property.tts.dictionaries': {
      handler(value) {
        if (this.ttsDictionaries.length > 0) this.setTtsDictionariesName()
      }
    },
    audioEnable: {
      handler(value) {
        this.property.default_input.beep.enable = value
      }
    },
    audioSelected: {
      handler(value) {
        this.property.default_input.beep.path = value

        const find = _.find(this.beepAudios, { path: value })
        if (find) {
          this.signedUrlSelect = find.sign_url
        }
      }
    }
  },
  mounted() {
    this.$refs.myAudio.addEventListener('play', () => {
      this.isPlaying = true
    })
    this.$refs.myAudio.addEventListener('ended', () => {
      this.isPlaying = false
    })
  },
  async created() {
    if (!this.node.property.default_input.beep) {
      this.node.property.default_input.beep = {
        enable: true,
        path: ''
      }
    }
    if (!this.node.property.default_input.asr) {
      this.node.property.default_input.asr = {}
    }
    this.node.property.default_input.asr = Object.assign(
      {
        timeout: '5.00',
        complete_timeout: '1.50',
        incomplete_timeout: '2.00',
        max_speech_timeout: '14.00',
        asr_confidence_threshold: '0.00',
        voice_barge_in: false,
        voice_barge_in_sensitivity: 4,
        voice_barge_in_threshold: 128
      },
      this.node.property.default_input.asr
    )
    if (!this.node.property.tts) {
      this.node.property.tts = {
        voice: '',
        speaker: '',
        speed: 1,
        pitch: 1,
        dictionaries: [],
        dictionaries_name: []
      }
    }
    if (!this.node.property.tts.dictionaries) {
      this.node.property.tts.dictionaries = []
    }
    if (!this.node.property.tts.dictionaries_name) {
      this.node.property.tts.dictionaries_name = []
    }
    if (!this.node.property.amd) {
      this.node.property.amd = {
        enable: false,
        max_duration: 10,
        max_silence: 3,
        machine_cutoff_duration: 5
      }
    } else {
      this.node.property.amd.max_duration = parseInt(
        this.node.property.amd.max_duration
      )
      this.node.property.amd.max_silence = parseInt(
        this.node.property.amd.max_silence
      )
      this.node.property.amd.machine_cutoff_duration = parseInt(
        this.node.property.amd.machine_cutoff_duration
      )
    }

    this.property = this.node.property

    // CHAT-2512 : อัพเดตการแสดงผล Max retries
    this.property.flow.fallback_after =
      parseInt(this.property.flow.fallback_after) + 1

    this.audioEnable = this.property.default_input.beep.enable
    this.audioSelected = this.property.default_input.beep.path

    const tabFilter = _.filter(this.tabs, o => o.name !== 'Home')
    this.tabFilter = _.sortBy(tabFilter, [
      function(o) {
        return o.name.toLowerCase()
      }
    ])

    await this.getAudio()

    const find = _.find(this.beepAudios, { path: this.audioSelected })
    if (find) {
      this.signedUrlSelect = find.sign_url
    }
    this.getTtsDictionaries()
  },
  methods: {
    async playPrompt(type) {
      if (this.typePlaying !== type) {
        if (this.isPlaying) this.isPlaying = false

        this.typePlaying = type
      }

      try {
        if (!this.isPlaying) {
          let preview
          if (type === 'tts') {
            this.ttsLoading = true

            const tts = await apiSynthesis.synthesis({
              ...this.property.tts,
              text: this.text,
              useCache: false
            })
            preview = tts.data.audio
          } else {
            preview = this.signedUrlSelect //`${this.minioHost}${this.property.default_input.beep.path}`
          }

          this.previewVoice = preview

          this.$refs.myAudio.load()

          setTimeout(() => {
            this.$refs.myAudio.play()
            this.ttsLoading = false
          }, 300)
        } else {
          this.$refs.myAudio.pause()
          this.isPlaying = false
        }
      } catch {}
    },
    async downloadPrompt(type) {
      try {
        if (type === 'tts') {
          this.ttsDownloading = true

          const data = await apiSynthesis.synthesis(
            {
              ...this.property.tts,
              text: this.text,
              useCache: false,
              download: true
            },
            'blob'
          )

          if (data) {
            let fileName = this.text.replace(/[/\\?%*:|"<>]/g, '')
            if (fileName.length > 50) fileName = fileName.substring(0, 50)

            const blob = new Blob([data], { type: 'audio/wav' })
            const link = document.createElement('a')
            link.href = URL.createObjectURL(blob)

            link.target = '_blank'
            link.download = `${fileName}.wav`
            link.click()
            URL.revokeObjectURL(link.href)
          }

          this.ttsDownloading = false
        }
      } catch {}
    },
    async getAudio() {
      try {
        this.audios = []

        const getData = await apiAudio.getAudio({ category: 'BEEP' })

        this.minioHost = getData.minio_url
        this.beepAudios = getData.data
      } catch {}
    },
    async getTtsDictionaries() {
      try {
        const ttsDictionaries = await apiTtsDictionaries.getTtsDictionaryList()
        this.ttsDictionaries = ttsDictionaries.data

        this.checkTtsDictionaries()
      } catch {}
    },
    checkTtsDictionaries() {
      if (this.property.tts.dictionaries.length) {
        for (let i = this.property.tts.dictionaries.length - 1; i >= 0; i--) {
          const tts_dictionary_id = this.property.tts.dictionaries[i]
          const ttsDictionary = _.find(this.ttsDictionaries, {
            tts_dictionary_id
          })
          if (!ttsDictionary) this.property.tts.dictionaries.splice(i, 1)
        }
      }
    },
    subString(text, length) {
      if (text.length < length) return text
      return `${text.substr(0, length)}...`
    },
    close() {
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      })
      this.errorMessage = ''
    },
    async save() {
      try {
        if (!this.editedItem._id && !this.editedItem.file) {
          this.errorMessage = 'Audio file is required'
          return false
        }

        this.editedItem.path = `beep/${this.editedItem.path}`
        // upload file
        if (this.editedItem.file) {
          const formData = new FormData()
          formData.append('path', this.editedItem.path)
          formData.append('file', this.editedItem.file)

          await apiAudio.uploadFileAudio(formData)

          // remove old
          const item = _.find(this.audios, { _id: this.editedItem._id })
          if (item && item.path && item.path !== this.editedItem.path) {
            await apiAudio.clearFileAudio({ path: item.path })
          }
        }

        delete this.editedItem.file

        if (this.editedIndex > -1) {
          const _id = this.editedItem._id

          const updateQueue = await apiAudio.updateAudio(
            this.search.category,
            _id,
            this.editedItem
          )

          this.snackbar.colorSnackbar = 'success'
          this.snackbar.textSnackbar = 'Changes saved'
          this.snackbar.open = true
        } else {
          const createQueue = await apiAudio.createAudio(this.editedItem)

          this.snackbar.colorSnackbar = 'success'
          this.snackbar.textSnackbar = `${this.editedItem.path} created`
          this.snackbar.open = true

          this.audioSelected = this.editedItem.path
        }

        this.getAudio()
        this.close()
      } catch (error) {
        this.errorMessage = error.message
      }
    },
    deleteAudio(path) {
      const item = _.find(this.beepAudios, { path })

      this.dialogConfirm.open = true
      this.dialogConfirm._id = item._id
      this.dialogConfirm.name = item.path
      this.dialogConfirm.action = 'DELETE'
    },

    async dialogConfirmClick() {
      const deleteAudio = await apiAudio.deleteAudio(this.dialogConfirm._id)
      if (deleteAudio.code === 0) {
        this.getAudio()
        this.close()

        this.snackbar.colorSnackbar = 'error'
        this.snackbar.textSnackbar = `${this.dialogConfirm.name} cleared`
        this.snackbar.open = true

        this.setDefaultDialogConfirm()
      } else {
        this.snackbar.colorSnackbar = 'error'
        this.snackbar.textSnackbar = deleteAudio.message
        this.snackbar.open = true
      }
    },
    setTtsDictionariesName() {
      this.property.tts.dictionaries_name = []
      this.property.tts.dictionaries.forEach(id => {
        const name = _.find(this.ttsDictionaries, { tts_dictionary_id: id })
          .name
        if (name) this.property.tts.dictionaries_name.push(name)
      })
    },
    setDefaultDialogConfirm() {
      this.dialogConfirm.open = false
      this.dialogConfirm._id = ''
      this.dialogConfirm.name = ''

      this.audioSelected = ''
    },
    setPropertyData(property) {
      if (property === 'fallback_after') {
        let val = this.property.flow[property]

        if (val < 1) {
          val = 1
        }

        this.property.flow[property] = parseInt(val)
      } else {
        let val = this.property.amd[property]

        if (val < 0) {
          val = 0
        }

        this.property.amd[property] = parseInt(val)
      }
    },
    goToDictionary() {
      const routeData = this.$router.resolve({
        path: '/settings/tts_dictionaries'
      })
      window.open(routeData.href, '_blank')
    }
  }
}
</script>

<style scoped lang="scss">
.display-inline-flex {
  display: inline-flex;
}
::v-deep .v-text-field__slot > input,
::v-deep .v-text-field__slot > .v-select__slot {
  margin: 0px !important;
  padding: 0px !important;
}

::v-deep .v-input__icon--clear .v-icon.v-icon {
  font-size: 16px !important;
}

.width50px {
  width: 50px;
}

.font-size-body {
  max-width: 300px;
  display: flex;
  text-align: justify;
  text-justify: inter-word;
  padding-left: 4px;
  margin-left: 4px;
  padding-right: 4px;
  margin-right: 4px;
  margin-bottom: 0px;
  word-wrap: break-word;
}

.row-center {
  display: flex;
  align-items: center;
}

.dropdown-title {
  font-size: 14px;
}

.dropdown-text {
  font-size: 11px;
  color: $text-color;
}

.slider-scroll {
  width: 290px;
  max-width: 290px;
}

.slider-text {
  width: 40px;
}

.m-l-align {
  margin-left: 20px;
}

.m-l-asr {
  margin-left: 32px;
}
</style>
